import { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, Send, MessageSquare, Plus, Hash, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useInternalChat, type ChatChannel } from '@/hooks/useInternalChat';
import { useAuth } from '@/contexts/AuthContext';
import { useAgentStatuses } from '@/hooks/useAgentStatus';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';

interface InternalChatPanelProps {
  onBack?: () => void;
  embedded?: boolean;
}

export function InternalChatPanel({ onBack, embedded }: InternalChatPanelProps) {
  const { user } = useAuth();
  const {
    channels, colleagues, messages, loading,
    getMessages, sendMessage, markRead, createDM,
  } = useInternalChat();
  const { data: agentStatuses } = useAgentStatuses();

  const isOnline = useCallback((userId: string) => {
    const status = (agentStatuses || []).find(s => s.user_id === userId);
    return !!status && status.status !== 'offline' &&
      new Date(status.updated_at).getTime() > Date.now() - 120_000;
  }, [agentStatuses]);

  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [localOptimistic, setLocalOptimistic] = useState<import('@/hooks/useInternalChat').ChatMessage[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const activeChannel = channels.find(ch => ch.id === activeChannelId);
  const hookMessages = activeChannelId ? messages[activeChannelId] || [] : [];

  const channelMessages = (() => {
    if (localOptimistic.length === 0) return hookMessages;
    const merged = [...hookMessages];
    for (const opt of localOptimistic) {
      const alreadyExists = merged.some(m =>
        m.content === opt.content && m.sender_id === opt.sender_id &&
        Math.abs(new Date(m.created_at).getTime() - new Date(opt.created_at).getTime()) < 10000
      );
      if (!alreadyExists) merged.push(opt);
    }
    return merged;
  })();

  useEffect(() => {
    if (localOptimistic.length > 0 && hookMessages.length > 0) {
      setLocalOptimistic(prev => prev.filter(opt =>
        !hookMessages.some(m =>
          m.content === opt.content && m.sender_id === opt.sender_id &&
          Math.abs(new Date(m.created_at).getTime() - new Date(opt.created_at).getTime()) < 10000
        )
      ));
    }
  }, [hookMessages, localOptimistic.length]);

  useEffect(() => {
    if (activeChannelId) {
      getMessages(activeChannelId);
      markRead(activeChannelId);
    }
  }, [activeChannelId, getMessages, markRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [channelMessages.length]);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || !activeChannelId || sending || !user?.id) return;
    const content = inputValue.trim();
    const optimisticMsg: import('@/hooks/useInternalChat').ChatMessage = {
      id: crypto.randomUUID(),
      channel_id: activeChannelId,
      sender_id: user.id,
      content,
      created_at: new Date().toISOString(),
      senderName: 'Dig',
    };
    setLocalOptimistic(prev => [...prev, optimisticMsg]);
    setInputValue('');
    setSending(true);
    await sendMessage(activeChannelId, content);
    setSending(false);
  }, [inputValue, activeChannelId, sending, sendMessage, user?.id]);

  const handleStartDM = useCallback(async (colleagueId: string) => {
    const channelId = await createDM(colleagueId);
    if (channelId) {
      setActiveChannelId(channelId);
      setShowNewChat(false);
    }
  }, [createDM]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'HH:mm', { locale: da });
    } catch { return ''; }
  };

  // When embedded in FloatingPanel, use full height flex layout without fixed positioning
  const wrapperClass = embedded
    ? "flex flex-col h-full w-full bg-background overflow-hidden"
    : "fixed bottom-4 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)] bg-background border border-border rounded-xl shadow-2xl overflow-hidden animate-scale-in flex flex-col";
  const wrapperStyle = embedded ? undefined : { height: '520px' };

  // ── New chat: colleague picker ──
  if (showNewChat) {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-primary/5">
          <button onClick={() => setShowNewChat(false)} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <span className="text-sm font-medium text-foreground">Ny besked</span>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {colleagues.map(col => (
              <button
                key={col.id}
                onClick={() => handleStartDM(col.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left"
              >
                <div className="relative shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                      {getInitials(col.full_name)}
                    </span>
                  </div>
                  <span className={cn(
                    "absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background",
                    isOnline(col.id) ? "bg-emerald-500" : "bg-muted-foreground"
                  )} />
                </div>
                <span className="text-sm text-foreground">{col.full_name}</span>
              </button>
            ))}
            {colleagues.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-8">Ingen kollegaer fundet</p>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // ── Active channel: message view ──
  if (activeChannel) {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-primary/5">
          <button onClick={() => setActiveChannelId(null)} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{activeChannel.displayName}</p>
            {activeChannel.type === 'direct' && (() => {
              const col = colleagues.find(c => c.full_name === activeChannel.displayName);
              const online = col ? isOnline(col.id) : false;
              return (
                <p className={cn("text-[10px]", online ? "text-emerald-500" : "text-muted-foreground")}>
                  {online ? "Online" : "Offline"}
                </p>
              );
            })()}
          </div>
        </div>

        <ScrollArea className="flex-1 px-3 py-2">
          <div className="space-y-3">
            {channelMessages.map(msg => {
              const isMe = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={cn("flex gap-2", isMe && "flex-row-reverse")}>
                  {!isMe && (
                    <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-muted-foreground">
                        {getInitials(msg.senderName)}
                      </span>
                    </div>
                  )}
                  <div className={cn("max-w-[75%] space-y-0.5", isMe && "items-end")}>
                    {!isMe && (
                      <p className="text-[10px] text-muted-foreground ml-1">{msg.senderName}</p>
                    )}
                    <div className={cn(
                      "rounded-xl px-3 py-2 text-sm",
                      isMe
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-secondary text-foreground rounded-bl-sm"
                    )}>
                      {msg.content}
                    </div>
                    <p className={cn("text-[9px] text-muted-foreground", isMe ? "text-right mr-1" : "ml-1")}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            {channelMessages.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-12">
                Ingen beskeder endnu. Sig hej! 👋
              </p>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        <div className="border-t border-border p-2">
          <div className="flex items-center gap-2">
            <input
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Skriv besked..."
              className="flex-1 bg-secondary rounded-lg px-3 py-2 text-sm outline-none text-foreground placeholder:text-muted-foreground"
              autoFocus
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || sending}
              className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Channel list ──
  const directChannels = channels.filter(ch => ch.type === 'direct');
  const groupChannels = channels.filter(ch => ch.type === 'group');

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
        <div className="flex items-center gap-2">
          {onBack && !embedded && (
            <button onClick={onBack} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center">
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
          <MessageSquare className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-foreground">Intern Chat</span>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {loading ? (
            <p className="text-xs text-muted-foreground text-center py-12">Indlæser...</p>
          ) : (
            <>
              {directChannels.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-1">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Direkte beskeder</span>
                  </div>
                  {directChannels.map(ch => (
                    <ChannelRow key={ch.id} channel={ch} icon="user" onClick={() => setActiveChannelId(ch.id)} isOnline={(() => { const col = colleagues.find(c => c.full_name === ch.displayName); return col ? isOnline(col.id) : undefined; })()} />
                  ))}
                </>
              )}

              {groupChannels.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-1">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Grupper</span>
                  </div>
                  {groupChannels.map(ch => (
                    <ChannelRow key={ch.id} channel={ch} icon="hash" onClick={() => setActiveChannelId(ch.id)} />
                  ))}
                </>
              )}

              {channels.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-12">
                  Ingen samtaler endnu
                </p>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      <div className="p-2 border-t border-border">
        <button
          onClick={() => setShowNewChat(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors text-sm font-medium text-primary"
        >
          <Plus className="w-4 h-4" />
          Ny besked
        </button>
      </div>
    </div>
  );
}

function ChannelRow({ channel, icon, onClick, isOnline }: { channel: ChatChannel; icon: 'user' | 'hash'; onClick: () => void; isOnline?: boolean }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left"
    >
      <div className="relative shrink-0">
        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
          {icon === 'user' ? (
            <User className="w-4 h-4 text-blue-500" />
          ) : (
            <Hash className="w-4 h-4 text-blue-500" />
          )}
        </div>
        {icon === 'user' && isOnline !== undefined && (
          <span className={cn(
            "absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background",
            isOnline ? "bg-emerald-500" : "bg-muted-foreground"
          )} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn("text-sm truncate", channel.unreadCount > 0 ? "font-semibold text-foreground" : "text-foreground")}>
          {channel.displayName}
        </p>
        {channel.lastMessage && (
          <p className="text-[10px] text-muted-foreground truncate">{channel.lastMessage}</p>
        )}
      </div>
      {channel.unreadCount > 0 && (
        <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center shrink-0">
          {channel.unreadCount}
        </span>
      )}
    </button>
  );
}
